import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Row, Col, Input, UncontrolledTooltip } from "reactstrap";
import {
    faArrowRight,
    faFileLines,
    faSearch,
    faThumbtack,
    faWrench,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { direction } from "Pages/Integration/Direction";
import {
    normalizeSystemKey,
    parseCompanySystems,
    resolveSystemIcon,
    useIsDarkTheme,
} from "Pages/Integration/systemIcons";
import useHelpers from "Hooks/useHelpers";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Integration/Components/IntegrationRow/IntegrationRow.module.css";
import ArgumentPicker from "../ArgumentPicker/ArgumentPicker";
import useAlertContext from "Hooks/useAlertContext";
import useUserContext from "Hooks/useUserContext";

interface IArgumentData {
    [key: string]: string;
}

function toSafeDomIdSegment(value: string): string {
    return value
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^A-Za-z0-9_-]/g, "-");
}

function normalizeDirection(directionIn: string | undefined): string {
    if (!directionIn) return "";

    let normalized = directionIn.replace(/([A-Z])/g, function ($1) {
        return "_" + $1.toLowerCase();
    });

    if (normalized.startsWith("_")) {
        normalized = normalized.substring(1);
    }

    return normalized.toLowerCase();
}

function normalizeSystemOverride(value: string | undefined): string | undefined {
    const normalized = normalizeSystemKey(value);
    return normalized || undefined;
}

// Subcomponent of Integrations.tsx
export default function IntegrationRow({
    row,
    index,
    array,
    pushRunToast,
    showGroupHeader = true,
    variant = "list",
    onToggleFavorite,
    isPinned = false,
    isPinSaving = false,
    instanceKey,
}: {
    row: IIntegration;
    index: number;
    array: IIntegration[];
    /** Adds a persistent toast for an inline job run (run_inline preference). */
    pushRunToast: (message: string) => void;
    /** Hides the default integration_group heading for tile mode. */
    showGroupHeader?: boolean;
    /** Rendering mode used for home list vs launch tiles. */
    variant?: "list" | "tile";
    /** Optional callback for pinning/unpinning this integration as a favorite. */
    onToggleFavorite?: (row: IIntegration) => void;
    /** Current favorite state for this row. */
    isPinned?: boolean;
    /** True while the preference save is in flight for this row. */
    isPinSaving?: boolean;
    /** Optional stable suffix to prevent duplicated DOM ids across multiple tiles. */
    instanceKey?: string;
}) {
    /** Generates default "" values for the unknown key names for argumentData below */
    const defaultArgumentDataState = row.arguments.reduce(
        (acc: IArgumentData, arg: IArgument) => {
            acc[arg.argument_number] = "";
            return acc;
        },
        {}
    );
    /** Holds all data about arguments in the integration for ArgumentPicker modal. Acts as state for argument inputs.
     * An object with keys as argument indexes and values as the input values.
     * @example { "0": "123", "1": "2023-11-05", "2": "2023-11-15" }
     */
    const [argumentData, setArgumentData] = useState<IArgumentData>(
        defaultArgumentDataState
    );
    /** Holds uploaded file used as integration argument */
    const [file, setFile] = useState<File | null>(null);
    /** GUID that gets appended to file name when adding integration to job queue */
    const [, setGuid] = useState("");
    /** Used to toggle ArgumentPicker modal and define its content */
    const [argumentPickerModal, setArgumentPickerModal] = useState<
        IArgument | undefined
    >(undefined);
    const isDarkTheme = useIsDarkTheme();

    const { newAlert } = useAlertContext();
    const [userContext] = useUserContext();

    const { handleFormChange } = useHelpers();
    const { post } = useFetch();

    const navigate = useNavigate();


    /** Used to upload files as integration arguments */
    const hiddenFileInput = useRef<HTMLInputElement>(null);

    /** Runs the integration on clicking the button */
    async function runIntegration(
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ): Promise<void> {
        // Require values for all argument inputs except the file input
        if (Object.values(argumentData).some((value) => value === "")) {
            newAlert({
                color: "danger",
                message: "Please fill in all arguments",
            });
            return;
        }

        // Build url and add arguments from the input values
        let url = `/api/jobqueue/${row.process}`;
        for (const arg of Object.values(argumentData)) {
            // 2026.03.03 - URI Component encoding arguments; I'm sure this won't break anything. --ndr
            // 2026.09.23 - it broke one thing: a '/' in an argument. ASP.NET Core leaves %2F
            // encoded in the catch-all route value, so the server re-encoded it to %252F
            // (dimension-nf33s). Fixed server-side in JobQueue.NormalizeMethodInfo, which is
            // where the single-encode contract the Runner expects is enforced. Keep this
            // encode - without it a '/' would split into an extra path segment. --dlw
            url += `/${encodeURIComponent(arg.trim())}`;
        }

        if (e.currentTarget.name === "DEBUG") url += "/DEBUG";

        console.log(`Adding integration to job queue. Url: ${url}`);
        const response = await post(url);

        let data;
        try {
            data = await response.json();
        } catch (error) {
            data = response;
        }
        console.log(data);

        if (response.ok) {
            // Opt-in per-user preference: when "run_inline" is on, stay on the home page and
            // just notify instead of redirecting to the review screen. Default (absent) = redirect.
            if (userContext.preferences?.run_inline === "true") {
                // Only append the arguments when there are any, so no-arg
                // integrations read "Queued: Send Vendors" (no empty parens).
                const args = Object.values(argumentData)
                    .map((v) => v.trim())
                    .filter(Boolean);
                const message = args.length
                    ? `Queued: ${row.name} — ${args.join(", ")}`
                    : `Queued: ${row.name}`;
                // Persistent stacking toast (not the shared auto-dismiss alert):
                // running several contracts leaves one toast each, up until
                // dismissed, so none are missed.
                pushRunToast(message);
                // Reset inputs so the next contract can be entered immediately
                setArgumentData(defaultArgumentDataState);
            } else {
                navigate("/run");
            }
        } else {
            newAlert({
                color: "danger",
                message: data.detail,
            });
        }
    }

    /** When button is clicked, it fires a click event on the hidden file input to trigger choose/upload process*/
    async function chooseFileUpload() {
        if (hiddenFileInput.current) {
            hiddenFileInput.current.click();
        }
    }

    /** Uploads a user selected file to AWS S3. Triggered by change event on hiddenFileInput. */
    async function uploadFile(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) return;

        const filePicked = e.target.files[0];

        // Reset file input value
        if (hiddenFileInput.current !== null)
            hiddenFileInput.current.value = "";

        const formData = new FormData();
        formData.append("file", filePicked);

        const response = await fetch(
            `/api/integrations/cloud/${filePicked.name}`,
            {
                method: "POST",
                body: formData,
            }
        );

        const data = await response.json();
        console.log("Upload file to S3 response: ", data);

        if (response.ok) {
            setFile(filePicked);
            setGuid(data);
            setArgumentData((prev) => ({
                ...prev,
                [e.target.name]: `${data}_${filePicked.name}`,
            }));
        } else {
            newAlert({
                color: "danger",
                message: "Error uploading file",
            });
        }
    }

    function toggleModal(argument: IArgument | undefined) {
        if (argumentPickerModal) {
            setArgumentPickerModal(undefined);
        } else {
            setArgumentPickerModal(argument);
        }
    }

    function onSetArgumentData(argument: any) {
        setArgumentData((prev) => ({
            ...prev,
            ...argument,
        }));
    }

    /** For input and button*/
    function isDisabled(argument: IArgument) {
        const boolean = Boolean(
            // Has data_source value, meaning it will use the argument picker modal
            // && Is not the first input
            // && Any of the inputs before it are empty
            argument.data_source &&
                argument.argument_number > 0 &&
                Object.entries(argumentData)
                    .slice(0, argument.argument_number)
                    .some(([, value]) => value === "")
        );
        return boolean;
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            runIntegration(e as any);
        }
    };

    const group = row.integration_group;
    const directionInfo = direction.getDirectionInfo(row.direction);
    const normalizedDirection = normalizeDirection(row.direction);
    const companySystems = Array.from(
        new Set(
            parseCompanySystems(userContext.companySystems).map(normalizeSystemKey)
        )
    );

    const companyPrimarySystem = companySystems[0];
    const companySecondarySystem = companySystems[1];

    const explicitSourceSystem = normalizeSystemOverride(row.from_system);
    const explicitTargetSystem = normalizeSystemOverride(row.to_system);
    const hasExplicitSystemPair =
        Boolean(explicitSourceSystem) && Boolean(explicitTargetSystem);

    const providerSourceSystem =
        normalizedDirection === "send" && companySecondarySystem
            ? companyPrimarySystem
            : normalizedDirection === "get" && companySecondarySystem
            ? companySecondarySystem
            : undefined;

    const providerTargetSystem =
        normalizedDirection === "send" && companySecondarySystem
            ? companySecondarySystem
            : normalizedDirection === "get" && companySecondarySystem
            ? companyPrimarySystem
            : undefined;

    const fallbackSourceSystem = providerSourceSystem;
    const fallbackTargetSystem = providerTargetSystem;

    const resolvedSourceSystem = hasExplicitSystemPair
        ? explicitSourceSystem
        : fallbackSourceSystem;
    const resolvedTargetSystem = hasExplicitSystemPair
        ? explicitTargetSystem
        : fallbackTargetSystem;

    let tileSourceSystemIconSrc = resolveSystemIcon(
        resolvedSourceSystem,
        isDarkTheme
    );
    let tileTargetSystemIconSrc = resolveSystemIcon(
        resolvedTargetSystem,
        isDarkTheme
    );

    // If explicit From/To values were set but one icon is missing, fall back to
    // legacy get/send mapping before defaulting to the direction arrow icon.
    if (
        hasExplicitSystemPair &&
        (!tileSourceSystemIconSrc || !tileTargetSystemIconSrc)
    ) {
        tileSourceSystemIconSrc = resolveSystemIcon(fallbackSourceSystem, isDarkTheme);
        tileTargetSystemIconSrc = resolveSystemIcon(fallbackTargetSystem, isDarkTheme);
    }
    const useSystemRunIcons =
        Boolean(tileSourceSystemIconSrc && tileTargetSystemIconSrc);

    const rawIdSuffix = instanceKey ?? `${row.id}-${index}`;
    const idSuffix = toSafeDomIdSegment(rawIdSuffix);

    const argumentPickerProps = {
        argument: argumentPickerModal,
        argumentData,
        modalOpen: argumentPickerModal !== undefined,
        onSetArgumentData,
        toggle: toggleModal,
    };

    const renderArgumentFields = () =>
        row.arguments.map((a, index) => {
            const type = a.name.toLowerCase().includes("file")
                ? "file"
                : a.name.toLowerCase().includes("date")
                ? "date"
                : "text";

            // Not a date or file input
            if (type === "text") {
                return (
                    <div
                        key={a.name + index}
                        className={`${styles.argumentBox} ${
                            variant === "tile" ? styles.tileArgumentBox : ""
                        }`}
                    >
                        <Input
                            name={a.argument_number.toString()}
                            value={argumentData[a.argument_number]}
                            onChange={(e) => handleFormChange(e, setArgumentData)}
                            onKeyDown={handleKeyDown}
                            type="text"
                            placeholder={a.name.replaceAll("_", " ")}
                            disabled={isDisabled(a)}
                        />
                        {a.data_source && (
                            <Button
                                onClick={() => toggleModal(a)}
                                disabled={isDisabled(a)}
                            >
                                <FontAwesomeIcon
                                    icon={faSearch}
                                    className={styles.fileChooser}
                                />
                            </Button>
                        )}
                    </div>
                );
            }

            // This is a file input
            if (type === "file") {
                return (
                    <div
                        key={a.name + index}
                        className={`${styles.argumentBox} ${
                            variant === "tile" ? styles.tileArgumentBox : ""
                        }`}
                    >
                        <Button
                            onClick={chooseFileUpload}
                            className={styles.uploadBtn}
                        >
                            {file ? file.name : "Upload File"}{" "}
                        </Button>

                        {/* Hidden file input */}
                        <input
                            ref={hiddenFileInput}
                            className={styles.hiddenFileInput}
                            name={a.argument_number.toString()}
                            onChange={uploadFile}
                            type="file"
                        />
                    </div>
                );
            }

            if (type === "date") {
                return (
                    <div
                        key={a.name + index}
                        className={`${styles.argumentBox} ${
                            variant === "tile" ? styles.tileArgumentBox : ""
                        }`}
                    >
                        <Input
                            name={a.argument_number.toString()}
                            value={argumentData[a.argument_number]}
                            onChange={(e) => handleFormChange(e, setArgumentData)}
                            onKeyDown={handleKeyDown}
                            type="date"
                            disabled={isDisabled(a)}
                        />
                    </div>
                );
            }
            return null;
        });

    return (
        <>
            <ArgumentPicker {...argumentPickerProps} />

            <Row
                key={index}
                className={`${styles.rowBox} ${
                    variant === "tile" ? styles.tileRowBox : ""
                }`}
                data-method={row.process}
            >
                {/* Integration Group */}
                {/* If this row's integration group is the first or is not the same as the last row's, show the group name */}
                {showGroupHeader &&
                    (index === 0 ||
                        group !== array[index - 1].integration_group) && (
                    <Row>
                        <Col>
                            <hr />
                            <h3>{row.integration_group}</h3>
                        </Col>
                    </Row>
                    )}

                {variant === "tile" ? (
                    <>
                        <div className={styles.tileHeaderBlock}>
                            {/* Run live */}
                            <Button
                                id={`runLiveBtn-${idSuffix}`}
                                className={`${styles.btn} ${styles.runBtn} btn-link`}
                                name=""
                                onClick={runIntegration}
                                disabled={row.disabled}
                            >
                                {useSystemRunIcons ? (
                                    <span
                                        data-testid={`runLiveSvg-${idSuffix}`}
                                        className={styles.liveRunIconRow}
                                    >
                                        <img
                                            src={tileSourceSystemIconSrc}
                                            alt="source system"
                                            className={styles.sourceSystemIcon}
                                        />
                                        <FontAwesomeIcon
                                            icon={faArrowRight}
                                            className={`${styles.liveRunArrow} ${styles.tileNeutralGlyph}`}
                                        />
                                        <img
                                            src={tileTargetSystemIconSrc}
                                            alt="target system"
                                            className={styles.targetSystemIcon}
                                        />
                                    </span>
                                ) : (
                                    <FontAwesomeIcon
                                        data-testid={`runLiveSvg-${idSuffix}`}
                                        className={styles.icon}
                                        icon={directionInfo.icon}
                                        style={{ color: directionInfo.color }}
                                    />
                                )}
                            </Button>
                            <UncontrolledTooltip target={`runLiveBtn-${idSuffix}`}>
                                {useSystemRunIcons ? 
                                (
                                    <span>Move data from {resolvedSourceSystem?.toUpperCase()} into {resolvedTargetSystem?.toUpperCase()}</span>
                                ) : directionInfo.title}
                            </UncontrolledTooltip>

                            {/* Run in Debug Mode */}
                            <Button
                                id={`runPreviewBtn-${idSuffix}`}
                                className={`${styles.btn} ${styles.runBtn} btn-link`}
                                name="DEBUG"
                                onClick={runIntegration}
                                disabled={row.disabled}
                            >
                                <FontAwesomeIcon
                                    data-testid={`runLiveSvg-${idSuffix}`}
                                    className={`${styles.icon} ${styles.tileNeutralGlyph}`}
                                    icon={faWrench}
                                />
                            </Button>
                            <UncontrolledTooltip target={`runPreviewBtn-${idSuffix}`}>
                                Preview Mode: {useSystemRunIcons ? 
                                (
                                    <span>Move data from {resolvedSourceSystem?.toUpperCase()} into {resolvedTargetSystem?.toUpperCase()}</span>
                                ) : directionInfo.title}
                            </UncontrolledTooltip>

                            {row.documentation_url && (
                                <>
                                    <Button
                                        id={`docLinkBtn-${idSuffix}`}
                                        tag="a"
                                        href={row.documentation_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`${styles.btn} btn-link`}
                                    >
                                        <FontAwesomeIcon
                                            icon={faFileLines}
                                            className={styles.icon}
                                        />
                                    </Button>
                                    <UncontrolledTooltip target={`docLinkBtn-${idSuffix}`}>
                                        Documentation
                                    </UncontrolledTooltip>
                                </>
                            )}

                            <h5 id={`name-${idSuffix}`} className={styles.integrationName}>
                                {row.name}
                            </h5>
                            <UncontrolledTooltip target={`name-${idSuffix}`}>
                                {row.process}
                            </UncontrolledTooltip>

                            {onToggleFavorite && (
                                <>
                                    <Button
                                        id={`favoriteBtn-${idSuffix}`}
                                        className={`${styles.btn} ${styles.favoriteBtn} btn-link`}
                                        onClick={() => onToggleFavorite(row)}
                                        disabled={isPinSaving}
                                    >
                                        <FontAwesomeIcon
                                            icon={faThumbtack}
                                            className={`${styles.pinIcon} ${
                                                isPinned ? styles.pinIconActive : ""
                                            }`}
                                        />
                                    </Button>
                                    <UncontrolledTooltip target={`favoriteBtn-${idSuffix}`}>
                                        {isPinned
                                            ? "Remove from Favorites"
                                            : "Pin to Favorites"}
                                    </UncontrolledTooltip>
                                </>
                            )}
                        </div>

                        {/* Argument Inputs */}
                        <div className={`${styles.inputsBox} ${styles.tileInputsBlock}`}>
                            {renderArgumentFields()}
                        </div>
                    </>
                ) : (
                    <>
                        {/* Integration Buttons and Name */}
                        <Col xs="auto" className={styles.btnsAndNameBox}>
                            {/* Run live */}
                            <Button
                                id={`runLiveBtn-${idSuffix}`}
                                className={`${styles.btn} btn-link`}
                                name=""
                                onClick={runIntegration}
                                disabled={row.disabled}
                            >
                                <FontAwesomeIcon
                                    data-testid={`runLiveSvg-${index}`}
                                    className={styles.icon}
                                    icon={directionInfo.icon}
                                    style={{ color: directionInfo.color }}
                                />
                            </Button>
                            <UncontrolledTooltip target={`runLiveBtn-${idSuffix}`}>
                                {directionInfo.title}
                            </UncontrolledTooltip>

                            {/* Run in Debug Mode */}
                            <Button
                                id={`runPreviewBtn-${idSuffix}`}
                                className={`${styles.btn} btn-link`}
                                name="DEBUG"
                                onClick={runIntegration}
                                disabled={row.disabled}
                            >
                                <FontAwesomeIcon
                                    data-testid={`runPreviewSvg-${index}`}
                                    className={styles.icon}
                                    icon={faWrench}
                                    style={{ color: directionInfo.color }}
                                />
                            </Button>
                            <UncontrolledTooltip target={`runPreviewBtn-${idSuffix}`}>
                                Preview Mode: {directionInfo.title}
                            </UncontrolledTooltip>

                            {row.documentation_url && (
                                <>
                                    <Button
                                        id={`docLinkBtn-${idSuffix}`}
                                        tag="a"
                                        href={row.documentation_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`${styles.btn} btn-link`}
                                    >
                                        <FontAwesomeIcon
                                            icon={faFileLines}
                                            className={styles.icon}
                                        />
                                    </Button>
                                    <UncontrolledTooltip target={`docLinkBtn-${idSuffix}`}>
                                        Documentation
                                    </UncontrolledTooltip>
                                </>
                            )}

                            <h5 id={`name-${idSuffix}`} className={styles.integrationName}>
                                {row.name}
                            </h5>
                            <UncontrolledTooltip target={`name-${idSuffix}`}>
                                {row.process}
                            </UncontrolledTooltip>
                        </Col>

                        {/* Argument Inputs */}
                        <Col className={styles.inputsBox}>{renderArgumentFields()}</Col>
                    </>
                )}
            </Row>
        </>
    );
}
